import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, geometry, outlined, taper } from '../effect-guns.sprite';

/**
 * Peacemaker (the Sheriff set's gun, ADR-0160 amended): a long-barrelled six-shooter held level. A walnut grip curving
 * down at its back, a brass frame engraved in gold, the hammer cocked over it, a fluted steel cylinder, a long blued
 * barrel with an ejector rod under it and a blade sight at the muzzle. On a shot (`fire`) the hammer falls, the muzzle
 * flashes and the cylinder turns a chamber (its flutes rolling by), then the hammer is cocked again.
 */

const cylinderTone = (y: number, roll: number): PaletteKey => {
  // Flutes rolling by as it turns: a dark groove every third row, lit along the top.
  if (y === 14) return 'w';
  if (y === 25) return '3';
  return (y + roll) % 3 === 0 ? '3' : y < 19 ? '5' : '4';
};

const revolver = (glint: number, roll: number, cocked: boolean, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The walnut grip curving down at its back, lit on its left, its grain dark, its pins in brass.
    for (let y = 19; y <= 31; y++) {
      const back = 3 + Math.round((y - 19) * 0.35);
      for (let x = back; x <= back + 8; x++)
        c.set(x, y, x === back ? 'm' : x > back + 6 ? 'N' : (x + y) % 5 === 0 ? 'N' : 'n');
    }
    c.set(7, 24, 'Y');
    c.set(9, 28, 'Y');
    // The brass frame, engraved: lit on top, a scroll of gold lines.
    box(c, 10, 15, 21, 24, [1], (x, y): PaletteKey => {
      if (y === 15) return 'y';
      if (y === 24) return 'U';
      return (x * 2 + y) % 5 === 0 ? 'y' : x > 19 ? 'U' : 'Y';
    });
    // The hammer: cocked back over the frame, or down.
    if (cocked) {
      c.set(10, 11, '4');
      c.set(11, 12, '4');
      c.set(12, 13, '3');
      c.set(11, 13, '3');
      c.set(12, 14, '3');
    } else c.set(12, 14, '4');
    // The trigger and its guard under the frame.
    box(
      c,
      13,
      25,
      19,
      28,
      [0, 1],
      (x, y): PaletteKey | null => (y === 28 || x === 19 ? 'U' : x === 15 ? '3' : null),
      'r',
    );
    // The fluted cylinder.
    box(c, 22, 14, 30, 25, [1], (_x, y) => cylinderTone(y, roll));
    // The long blued barrel, a glint running along its top; the ejector rod under it.
    taper(c, 31, 61, 18, 1.5, 1.5, (top, bottom, x): PaletteKey => {
      if (top === 0) return Math.abs(x - glint) < 2 ? 'w' : '5';
      if (bottom === 0) return '2';
      return x % 9 === 4 ? 'Y' : '3';
    });
    box(c, 31, 21, 46, 21, [0], (x): PaletteKey => (x === 46 ? 'Y' : '3'));
    // The blade sight and the muzzle, flashing on a shot.
    c.set(59, 15, '5');
    for (let y = 16; y <= 20; y++) {
      c.set(62, y, heat >= 2 ? 'w' : heat === 1 ? 'y' : '3');
      c.set(63, y, heat >= 2 ? 'w' : heat === 1 ? 'Y' : '2');
    }
  });

export default sprite({
  name: 'pollinator_sheriff',
  ...geometry,
  notes: `Peacemaker (ADR-0160 amended, the Sheriff set's gun): a long-barrelled six-shooter held level. A walnut grip
curving down at its back (lit on its left, brass pins), a brass frame engraved in gold, the hammer cocked over it, a
trigger in a brass guard, a fluted steel cylinder, a long slim steel barrel, gold bands round it, with a glint running along its top (4 frames) and
an ejector rod under it, a blade sight at the muzzle. Fire: the hammer falls, the muzzle flashes white then gold and the
cylinder's flutes roll round a chamber, the hammer cocked again, over 4 frames. Its flair: a wisp of smoke off the
muzzle, a glint. No team keys. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: {
      fps: 8,
      frames: [revolver(34, 0, true), revolver(42, 0, true), revolver(50, 0, true), revolver(58, 0, true)],
    },
    fire: {
      fps: 20,
      loop: false,
      frames: [revolver(0, 1, false, 2), revolver(0, 2, false, 1), revolver(0, 0, false), revolver(0, 0, true)],
    },
  },
});
